import { crm, CrmAvatar, Pill } from "../../../theme";
import {
  Box,
  Flex,
  CloseButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Button,
  ModalHeader,
  ModalFooter,
  Text,
  Checkbox,
  Input,
  InputGroup,
  InputLeftElement,
  useToast,
  FormLabel,
  Select,
  Spinner,
} from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { UserCommand } from "../../../models/User";
import { CompanyFormData } from "./types";
import { getCompanies } from "../../../services/companyService";
import { useCompany } from "../../../contexts/CompanyContext";
import { manageClients } from "../../../services/userService";
import { positions } from "../../../models/PurchaseOrder";
import SearchableSelect from "../../../components/SearchableSelect";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { accessibleScreenOptions, screenModules } from "../../../utils/screens";
import { getTeams } from "../../../services/teamService";
import { TeamViewModel } from "../../../models/Team";

type SectionKey = "profile" | "screens" | "clients";

const Icon = ({ name, size = 18 }: { name: string; size?: number }) => (
  <Box
    as="span"
    className="material-symbols-outlined"
    fontSize={`${size}px`}
    lineHeight="1"
    display="inline-flex"
  >
    {name}
  </Box>
);

const FLabel = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="xs" fontWeight="600" color={crm.textMuted} mb="6px">
    {children}
  </FormLabel>
);

const fieldStyle = {
  h: "42px",
  bg: crm.surface,
  borderColor: crm.border,
  borderRadius: "10px",
  fontSize: "sm" as const,
  _hover: { borderColor: crm.borderStrong },
  _focus: { borderColor: crm.brand, boxShadow: `0 0 0 1px ${crm.brand}` },
};

const scrollStyle = {
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-thumb": {
    background: crm.borderStrong,
    borderRadius: "8px",
  },
  "&::-webkit-scrollbar-track": { background: "transparent" },
};

/** Card wrapper used for each block of the right pane. */
const Section = ({
  title,
  hint,
  action,
  fill,
  children,
}: {
  title: string;
  hint?: string;
  action?: React.ReactNode;
  /** Ocupa toda a altura do pane e deixa o filho `flex=1` rolar sozinho. */
  fill?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    bg={crm.surface}
    border={`1px solid ${crm.border}`}
    borderRadius={crm.radius}
    boxShadow={crm.shadowSm}
    p="18px"
    mb={fill ? "0" : "16px"}
    {...(fill && {
      display: "flex",
      flexDirection: "column",
      flex: "1",
      minH: 0,
    })}
  >
    <Flex align="center" justify="space-between" gap="12px" mb="14px" flexShrink={0}>
      <Box>
        <Text fontSize="sm" fontWeight="700" color={crm.text}>
          {title}
        </Text>
        {hint && (
          <Text fontSize="xs" color={crm.textFaint} mt="2px">
            {hint}
          </Text>
        )}
      </Box>
      {action}
    </Flex>
    {children}
  </Box>
);

/** Left-rail navigation button. */
const RailItem = ({
  icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: string;
  label: string;
  count?: number;
  active: boolean;
  onClick: () => void;
}) => (
  <Flex
    as="button"
    type="button"
    onClick={onClick}
    align="center"
    gap="10px"
    w="100%"
    textAlign="left"
    px="12px"
    py="10px"
    borderRadius="10px"
    transition="all .15s"
    bg={active ? crm.surface : "transparent"}
    color={active ? crm.brand : crm.textMuted}
    boxShadow={active ? crm.shadowSm : "none"}
    fontWeight={active ? "700" : "500"}
    _hover={{ bg: active ? crm.surface : "rgba(15,23,42,.04)", color: crm.text }}
  >
    <Icon name={icon} />
    <Text fontSize="sm" flex="1" style={{ textTransform: "none" }}>
      {label}
    </Text>
    {count !== undefined && (
      <Box
        minW="22px"
        px="6px"
        py="1px"
        borderRadius="full"
        fontSize="11px"
        fontWeight="700"
        textAlign="center"
        bg={active ? crm.brandSoft : crm.columnBg}
        color={active ? crm.brand : crm.textMuted}
      >
        {count}
      </Box>
    )}
  </Flex>
);

/** Selectable tile (screens grid). */
const SelectTile = ({
  icon,
  title,
  selected,
  onToggle,
}: {
  icon?: string;
  title: string;
  selected: boolean;
  onToggle: () => void;
}) => (
  <Flex
    as="button"
    type="button"
    onClick={onToggle}
    align="center"
    gap="10px"
    p="12px"
    borderRadius="10px"
    textAlign="left"
    transition="all .15s"
    border={`1px solid ${selected ? crm.brand : crm.border}`}
    bg={selected ? crm.brandSoft : crm.surface}
    color={selected ? crm.brand : crm.textMuted}
    _hover={{ borderColor: selected ? crm.brand : crm.borderStrong }}
  >
    <Flex
      align="center"
      justify="center"
      w="32px"
      h="32px"
      flexShrink={0}
      borderRadius="8px"
      bg={selected ? crm.brand : crm.columnBg}
      color={selected ? "white" : crm.textMuted}
    >
      <Icon name={icon ?? "web_asset"} size={18} />
    </Flex>
    <Text
      flex="1"
      fontSize="sm"
      fontWeight={selected ? "600" : "500"}
      color={selected ? crm.text : crm.textMuted}
      style={{ textTransform: "none" }}
      noOfLines={1}
    >
      {title}
    </Text>
    <Flex
      align="center"
      justify="center"
      w="18px"
      h="18px"
      flexShrink={0}
      borderRadius="full"
      border={`1px solid ${selected ? crm.brand : crm.borderStrong}`}
      bg={selected ? crm.brand : "transparent"}
      color="white"
    >
      {selected && <Icon name="check" size={12} />}
    </Flex>
  </Flex>
);

const ManageClientsModal = ({
  isOpen,
  onClose,
  user,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: UserCommand | undefined;
}) => {
  const auth = useAuthUser<any>();
  const [section, setSection] = useState<SectionKey>("profile");
  const [position, setPosition] = useState(user?.position ?? 0);
  const [clients, setClients] = useState<CompanyFormData[]>([]);
  const [company, setCompany] = useState(user?.companyId);
  const [isAdmin, setIsAdmin] = useState(user?.admin);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(true);
  const [selectedClients, setSelectedClients] = useState<string[]>(
    user?.clients?.map((c) => c.companyId) ?? []
  );
  const [selectedScreens, setSelectedScreens] = useState<string[]>(
    user?.screens ?? []
  );
  const [teams, setTeams] = useState<TeamViewModel[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(
    user?.teamId
  );
  const toast = useToast();

  const { currentCompany } = useCompany();
  // Equipe é conceito da Distribuidora, mas usuário de empresa "Geral" enxerga tudo.
  const canManageTeam = currentCompany === 2 || auth?.company === "Geral";
  const isSuperAdmin = auth?.position === 2;
  // Empresa define as unidades que o usuário vê: só a empresa Geral altera (o backend também exige).
  const canChangeCompany = auth?.company === "Geral";

  useEffect(() => {
    const getClients = async () => {
      setLoadingClients(true);
      const response = await getCompanies(true, currentCompany);
      if (response) setClients(response);
      setLoadingClients(false);
    };
    getClients();
  }, []);

  useEffect(() => {
    if (!canManageTeam) return;
    const loadTeams = async () => {
      const response = await getTeams();
      setTeams(response ?? []);
    };
    loadTeams();
  }, [canManageTeam]);

  useEffect(() => {
    if (user) {
      setCompany(user.companyId);
      setPosition(user.position ?? 0);
      setIsAdmin(user.admin);
      setSelectedClients(user?.clients?.map((c) => c.companyId) ?? []);
      setSelectedScreens(user?.screens ?? []);
      setSelectedTeamId(user?.teamId);
    }
  }, [user]);

  // Ao (re)abrir volta pra primeira seção e limpa a busca.
  useEffect(() => {
    if (isOpen) {
      setSection("profile");
      setSearch("");
    }
  }, [isOpen]);

  const filteredClients = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        (c.cnpj ?? "").toLowerCase().includes(term)
    );
  }, [clients, search]);

  const allFilteredSelected =
    filteredClients.length > 0 &&
    filteredClients.every((c) => selectedClients.includes(c.id));

  const toggleAllFiltered = (checked: boolean) => {
    const ids = filteredClients.map((c) => c.id);
    setSelectedClients((prev) =>
      checked
        ? Array.from(new Set([...prev, ...ids]))
        : prev.filter((id) => !ids.includes(id))
    );
  };

  const toggleClient = (id: string) =>
    setSelectedClients((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );

  const toggleScreen = (link: string) =>
    setSelectedScreens((prev) =>
      prev.includes(link) ? prev.filter((s) => s !== link) : [...prev, link]
    );

  const allScreensSelected =
    selectedScreens.length > 0 &&
    accessibleScreenOptions.every((o) => selectedScreens.includes(o.link));

  const userName =
    `${user?.firstname ?? ""} ${user?.lastname ?? ""}`.trim() ||
    user?.email ||
    "Usuário";
  const positionLabel = positions.find((p) => p.key === position)?.label;
  const companyName = clients.find((c) => c.id === company)?.name;
  const selectedClientNames = clients.filter((c) =>
    selectedClients.includes(c.id)
  );

  const saveClients = async () => {
    setLoading(true);

    const response = await manageClients({
      clientsIds: selectedClients,
      userId: user?.id ?? "",
      position,
      companyId: company || undefined,
      admin: isAdmin ?? false,
      screens: selectedScreens,
      teamId: canManageTeam ? selectedTeamId : undefined,
    });
    if (response && response.data?.success === false) {
      toast({
        title: "Não foi possível editar o usuário.",
        description: (response.data.notifications ?? []).join(" "),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }
    if (response) {
      const companyChanged = !!company && company !== user?.companyId;
      toast({
        title: "Usuário editado.",
        description: companyChanged
          ? "Se o usuário estiver logado, ele precisa sair e entrar de novo para a nova empresa valer."
          : undefined,
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      onClose();
      return;
    }
    setLoading(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay backdropFilter="blur(3px)" bg="blackAlpha.600" />
      <ModalContent
        borderRadius="16px"
        overflow="hidden"
        boxShadow={crm.shadowLg}
        h="86vh"
        maxH="860px"
      >
        {/* ── Header ─────────────────────────────────────────────── */}
        <ModalHeader
          p="20px 24px"
          bgGradient={`linear(to-r, ${crm.brandSoft}, ${crm.surface})`}
          borderBottom={`1px solid ${crm.border}`}
        >
          <Flex align="center" gap="14px">
            <CrmAvatar name={userName} size={44} />
            <Box flex="1" minW={0}>
              <Text
                fontSize="md"
                fontWeight="700"
                color={crm.text}
                noOfLines={1}
                style={{ textTransform: "none" }}
              >
                {userName}
              </Text>
              <Flex align="center" gap="8px" mt="4px" flexWrap="wrap">
                <Text fontSize="xs" color={crm.textMuted} noOfLines={1}>
                  {user?.email}
                </Text>
                {isAdmin && (
                  <Pill bg={crm.brandSoft} color={crm.brand} borderColor={crm.brand}>
                    Admin
                  </Pill>
                )}
                {positionLabel && <Pill>{positionLabel}</Pill>}
                {companyName && <Pill>{companyName}</Pill>}
              </Flex>
            </Box>
            <CloseButton onClick={onClose} />
          </Flex>
        </ModalHeader>

        {/* ── Body: rail + pane ──────────────────────────────────── */}
        <ModalBody p="0" flex="1" minH={0} overflow="hidden">
          <Flex h="100%">
            {/* rail */}
            <Box
              w="230px"
              flexShrink={0}
              bg={crm.subtle}
              borderRight={`1px solid ${crm.border}`}
              p="16px 12px"
              display={{ base: "none", md: "block" }}
            >
              <Text
                fontSize="10px"
                fontWeight="700"
                letterSpacing=".08em"
                color={crm.textFaint}
                px="12px"
                mb="8px"
              >
                CONFIGURAÇÕES
              </Text>
              <Flex direction="column" gap="4px">
                <RailItem
                  icon="badge"
                  label="Perfil & acesso"
                  active={section === "profile"}
                  onClick={() => setSection("profile")}
                />
                <RailItem
                  icon="grid_view"
                  label="Telas"
                  count={selectedScreens.length}
                  active={section === "screens"}
                  onClick={() => setSection("screens")}
                />
                <RailItem
                  icon="apartment"
                  label="Clientes"
                  count={selectedClients.length}
                  active={section === "clients"}
                  onClick={() => setSection("clients")}
                />
              </Flex>
            </Box>

            {/* pane */}
            <Box
              flex="1"
              minW={0}
              bg={crm.pageBg}
              display="flex"
              flexDirection="column"
              // Em "Clientes" quem rola é a lista interna — evita scroll duplo.
              overflowY={section === "clients" ? "hidden" : "auto"}
              p="20px 24px"
              sx={scrollStyle}
            >
              {/* mobile section switcher */}
              <Flex
                gap="6px"
                mb="16px"
                display={{ base: "flex", md: "none" }}
                bg={crm.columnBg}
                p="4px"
                borderRadius="10px"
              >
                {(
                  [
                    ["profile", "Perfil"],
                    ["screens", "Telas"],
                    ["clients", "Clientes"],
                  ] as [SectionKey, string][]
                ).map(([key, label]) => (
                  <Button
                    key={key}
                    flex="1"
                    size="sm"
                    borderRadius="8px"
                    fontSize="xs"
                    bg={section === key ? crm.surface : "transparent"}
                    color={section === key ? crm.brand : crm.textMuted}
                    boxShadow={section === key ? crm.shadowSm : "none"}
                    _hover={{ bg: crm.surface }}
                    onClick={() => setSection(key)}
                  >
                    {label}
                  </Button>
                ))}
              </Flex>

              {/* ── Perfil ─────────────────────────────────────── */}
              {section === "profile" && (
                <>
                  {canChangeCompany && (
                    <Section
                      title="Empresa"
                      hint={
                        position <= 1
                          ? "Empresa à qual este usuário pertence. Para usuário cliente, ela define quais processos ele enxerga."
                          : "Empresa à qual este usuário pertence."
                      }
                    >
                      {clients.length > 0 && user && (
                        <SearchableSelect
                          defaultSelectValue={{
                            value: company,
                            label: clients.find((c) => c.id === company)?.name,
                            subLabel: "",
                          }}
                          options={clients.map((c) => ({
                            value: c.id,
                            label: c.name,
                            subLabel: c.cnpj,
                          }))}
                          name="clientId"
                          setValue={(value) => setCompany(value)}
                        />
                      )}
                    </Section>
                  )}

                  <Section
                    title="Cargo e equipe"
                    hint="Define o conjunto de permissões base do usuário."
                  >
                    <Flex gap="14px" flexWrap="wrap">
                      <Box flex="1" minW="200px">
                        <FLabel>Posição</FLabel>
                        <Select
                          value={position}
                          onChange={(e) =>
                            setPosition(parseFloat(e.target.value))
                          }
                          {...fieldStyle}
                        >
                          {positions
                            .filter((p) => isSuperAdmin || p.key > 2)
                            .map((p) => (
                              <option key={p.key} value={p.key}>
                                {p.label}
                              </option>
                            ))}
                        </Select>
                      </Box>
                      {canManageTeam && (
                        <Box flex="1" minW="200px">
                          <FLabel>Equipe</FLabel>
                          <Select
                            value={selectedTeamId ?? ""}
                            onChange={(e) =>
                              setSelectedTeamId(e.target.value || undefined)
                            }
                            {...fieldStyle}
                          >
                            <option value="">
                              Sem equipe (acessa todos os clientes)
                            </option>
                            {teams.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.name}
                              </option>
                            ))}
                          </Select>
                        </Box>
                      )}
                    </Flex>
                  </Section>

                  {isSuperAdmin && (
                    <Box
                      as="button"
                      type="button"
                      onClick={() => setIsAdmin(!isAdmin)}
                      w="100%"
                      textAlign="left"
                      p="16px 18px"
                      borderRadius={crm.radius}
                      transition="all .15s"
                      border={`1px solid ${isAdmin ? crm.brand : crm.border}`}
                      bg={isAdmin ? crm.brandSoft : crm.surface}
                      boxShadow={crm.shadowSm}
                      _hover={{
                        borderColor: isAdmin ? crm.brand : crm.borderStrong,
                      }}
                    >
                      <Flex align="center" gap="12px">
                        <Flex
                          align="center"
                          justify="center"
                          w="38px"
                          h="38px"
                          flexShrink={0}
                          borderRadius="10px"
                          bg={isAdmin ? crm.brand : crm.columnBg}
                          color={isAdmin ? "white" : crm.textMuted}
                        >
                          <Icon name="shield_person" size={20} />
                        </Flex>
                        <Box flex="1">
                          <Text fontSize="sm" fontWeight="700" color={crm.text}>
                            Administrador
                          </Text>
                          <Text fontSize="xs" color={crm.textMuted} mt="2px">
                            Acesso total ao sistema, ignora as restrições abaixo.
                          </Text>
                        </Box>
                        <Checkbox
                          isChecked={isAdmin ?? false}
                          pointerEvents="none"
                          colorScheme="blue"
                          size="lg"
                        />
                      </Flex>
                    </Box>
                  )}
                </>
              )}

              {/* ── Telas ──────────────────────────────────────── */}
              {section === "screens" && (
                <Section
                  title="Telas que o usuário pode acessar"
                  hint={`${accessibleScreenOptions.filter((o) => selectedScreens.includes(o.link)).length} de ${accessibleScreenOptions.length} selecionadas`}
                  action={
                    <Button
                      size="sm"
                      variant="ghost"
                      color={crm.brand}
                      borderRadius="8px"
                      fontSize="xs"
                      _hover={{ bg: crm.brandSoft }}
                      onClick={() =>
                        setSelectedScreens(
                          allScreensSelected
                            ? []
                            : accessibleScreenOptions.map((o) => o.link)
                        )
                      }
                    >
                      {allScreensSelected ? "Limpar" : "Selecionar todas"}
                    </Button>
                  }
                >
                  {screenModules.map((module, index) => {
                    const links = module.screens.map((o) => o.link);
                    const selectedCount = links.filter((link) => selectedScreens.includes(link)).length;
                    const allSelected = selectedCount === links.length;
                    return (
                      <Box
                        key={module.title}
                        pt={index > 0 ? "16px" : "0"}
                        mt={index > 0 ? "16px" : "0"}
                        borderTop={index > 0 ? `1px solid ${crm.border}` : undefined}
                      >
                        <Flex align="center" justify="space-between" gap="12px" mb="10px">
                          <Flex align="baseline" gap="8px" wrap="wrap">
                            <Text fontSize="xs" fontWeight="700" color={crm.text} textTransform="uppercase" letterSpacing="0.04em">
                              {module.title}
                            </Text>
                            <Text fontSize="xs" color={crm.textFaint}>
                              {selectedCount} de {links.length}
                              {module.hint ? ` · ${module.hint}` : ""}
                            </Text>
                          </Flex>
                          <Button
                            size="xs"
                            variant="ghost"
                            color={crm.brand}
                            borderRadius="8px"
                            _hover={{ bg: crm.brandSoft }}
                            onClick={() =>
                              setSelectedScreens((current) =>
                                allSelected
                                  ? current.filter((link) => !links.includes(link))
                                  : Array.from(new Set([...current, ...links]))
                              )
                            }
                          >
                            {allSelected ? "Limpar módulo" : "Selecionar módulo"}
                          </Button>
                        </Flex>
                        <Box
                          display="grid"
                          gridTemplateColumns={{
                            base: "1fr",
                            md: "repeat(2, 1fr)",
                            xl: "repeat(3, 1fr)",
                          }}
                          gap="10px"
                        >
                          {module.screens.map((o) => (
                            <SelectTile
                              key={o.link}
                              icon={o.icon}
                              title={o.title}
                              selected={selectedScreens.includes(o.link)}
                              onToggle={() => toggleScreen(o.link)}
                            />
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Section>
              )}

              {/* ── Clientes ───────────────────────────────────── */}
              {section === "clients" && (
                <Section
                  fill
                  title="Clientes que o usuário pode acessar"
                  hint={`${selectedClients.length} de ${clients.length} selecionados`}
                  action={
                    selectedClients.length > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        color={crm.textMuted}
                        borderRadius="8px"
                        fontSize="xs"
                        _hover={{ bg: crm.columnBg, color: crm.danger }}
                        onClick={() => setSelectedClients([])}
                      >
                        Limpar seleção
                      </Button>
                    )
                  }
                >
                  <InputGroup mb="12px" flexShrink={0}>
                    <InputLeftElement h="42px" color={crm.textFaint}>
                      <Icon name="search" />
                    </InputLeftElement>
                    <Input
                      placeholder="Pesquise por nome ou CNPJ"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      {...fieldStyle}
                    />
                  </InputGroup>

                  {/* chips dos selecionados */}
                  {selectedClientNames.length > 0 && (
                    <Flex gap="6px" flexWrap="wrap" mb="12px" flexShrink={0} maxH="88px" overflowY="auto" sx={scrollStyle}>
                      {selectedClientNames.slice(0, 8).map((c) => (
                        <Flex
                          key={c.id}
                          as="button"
                          type="button"
                          onClick={() => toggleClient(c.id)}
                          align="center"
                          gap="4px"
                          px="8px"
                          py="3px"
                          borderRadius="full"
                          fontSize="11px"
                          fontWeight="500"
                          bg={crm.brandSoft}
                          color={crm.brand}
                          border={`1px solid ${crm.brand}`}
                          _hover={{ bg: crm.brand, color: "white" }}
                        >
                          <Text noOfLines={1} maxW="180px">
                            {c.name}
                          </Text>
                          <Icon name="close" size={12} />
                        </Flex>
                      ))}
                      {selectedClientNames.length > 8 && (
                        <Pill>+{selectedClientNames.length - 8}</Pill>
                      )}
                    </Flex>
                  )}

                  <Flex
                    align="center"
                    justify="space-between"
                    px="12px"
                    py="8px"
                    mb="8px"
                    flexShrink={0}
                    borderRadius="8px"
                    bg={crm.subtle}
                  >
                    <Checkbox
                      isChecked={allFilteredSelected}
                      isDisabled={filteredClients.length === 0}
                      colorScheme="blue"
                      onChange={(e) => toggleAllFiltered(e.target.checked)}
                    >
                      <Text fontSize="xs" fontWeight="600" color={crm.textMuted}>
                        {search
                          ? "Selecionar todos os resultados"
                          : "Selecionar todos"}
                      </Text>
                    </Checkbox>
                    <Text fontSize="xs" color={crm.textFaint}>
                      {filteredClients.length}{" "}
                      {filteredClients.length === 1 ? "cliente" : "clientes"}
                    </Text>
                  </Flex>

                  <Box
                    flex="1"
                    minH="120px"
                    overflowY="auto"
                    border={`1px solid ${crm.border}`}
                    borderRadius="10px"
                    sx={scrollStyle}
                  >
                    {loadingClients ? (
                      <Flex align="center" justify="center" py="48px" gap="10px">
                        <Spinner size="sm" color={crm.brand} />
                        <Text fontSize="sm" color={crm.textMuted}>
                          Carregando clientes…
                        </Text>
                      </Flex>
                    ) : filteredClients.length === 0 ? (
                      <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        py="48px"
                        gap="6px"
                        color={crm.textFaint}
                      >
                        <Icon name="search_off" size={28} />
                        <Text fontSize="sm">Nenhum cliente encontrado</Text>
                        <Text fontSize="xs">Tente outro termo de busca.</Text>
                      </Flex>
                    ) : (
                      filteredClients.map((c, i) => {
                        const checked = selectedClients.includes(c.id);
                        return (
                          <Flex
                            key={c.id}
                            as="button"
                            type="button"
                            onClick={() => toggleClient(c.id)}
                            align="center"
                            gap="10px"
                            w="100%"
                            textAlign="left"
                            px="12px"
                            py="10px"
                            transition="background .12s"
                            borderTop={i === 0 ? "none" : `1px solid ${crm.border}`}
                            bg={checked ? crm.brandSoft : crm.surface}
                            _hover={{ bg: checked ? crm.brandSoft : crm.subtle }}
                          >
                            <Checkbox
                              isChecked={checked}
                              pointerEvents="none"
                              colorScheme="blue"
                            />
                            <Box flex="1" minW={0}>
                              <Text
                                fontSize="sm"
                                fontWeight={checked ? "600" : "500"}
                                color={crm.text}
                                noOfLines={1}
                                style={{ textTransform: "none" }}
                              >
                                {c.name}
                              </Text>
                              {c.cnpj && (
                                <Text fontSize="11px" color={crm.textFaint}>
                                  {c.cnpj}
                                </Text>
                              )}
                            </Box>
                          </Flex>
                        );
                      })
                    )}
                  </Box>
                </Section>
              )}
            </Box>
          </Flex>
        </ModalBody>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <ModalFooter
          borderTop={`1px solid ${crm.border}`}
          bg={crm.surface}
          px="24px"
        >
          <Flex w="100%" align="center" justify="space-between" gap="12px">
            <Flex align="center" gap="8px" color={crm.textMuted} fontSize="xs">
              <Icon name="tune" size={16} />
              <Text>
                {isAdmin
                  ? "Administrador — acesso irrestrito"
                  : `${selectedScreens.length} ${
                      selectedScreens.length === 1 ? "tela" : "telas"
                    } · ${selectedClients.length} ${
                      selectedClients.length === 1 ? "cliente" : "clientes"
                    }`}
              </Text>
            </Flex>
            <Flex gap="12px">
              <Button
                isDisabled={loading}
                variant="outline"
                borderColor={crm.border}
                color={crm.textMuted}
                borderRadius="10px"
                _hover={{ bg: crm.subtle, borderColor: crm.borderStrong }}
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                isLoading={loading}
                backgroundColor={crm.brand}
                color="white"
                borderRadius="10px"
                px="24px"
                _hover={{ backgroundColor: crm.brandDark }}
                onClick={saveClients}
              >
                Salvar alterações
              </Button>
            </Flex>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ManageClientsModal;
